export type StoredTokenObject = {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  type: 'Bearer';
  expires_in: number;
  token_expires_at: number; // epoch seconds
};

type StorageKey = 'token' | 'timeOut' | 'kcUser' | 'appUser' | 'appUserInactive' | 'kcSessionState';
// `justLoggedOut` is a short-lived flag used to avoid immediately re-authing via check-sso after logout.
type InternalStorageKey = StorageKey | 'justLoggedOut';

const PREFIX = 'agd.';
export const AUTH_STORAGE_EVENT = 'agd.auth-storage';

// Role label / active-role id must stay tab-local so SWITCH_ROLE in one tab does not
// bleed into others (matches sessionStorage RBAC snapshot scoping).
const TAB_SCOPED_KEYS: ReadonlySet<InternalStorageKey> = new Set(['kcUser', 'appUser']);

function storageFor(key: InternalStorageKey): Storage {
  return TAB_SCOPED_KEYS.has(key) ? sessionStorage : localStorage;
}

export function setStorage<T>(key: InternalStorageKey, value: T): void {
  const k = `${PREFIX}${key}`;
  try {
    storageFor(key).setItem(k, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(AUTH_STORAGE_EVENT, { detail: { key } }));
  } catch {
    // ignore storage failures
  }
}

export function getStorage<T>(key: InternalStorageKey): T | null {
  const k = `${PREFIX}${key}`;
  try {
    const raw = storageFor(key).getItem(k);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function removeStorage(key: InternalStorageKey): void {
  const k = `${PREFIX}${key}`;
  try {
    storageFor(key).removeItem(k);
    window.dispatchEvent(new CustomEvent(AUTH_STORAGE_EVENT, { detail: { key } }));
  } catch {
    // ignore
  }
}

export function isJustLoggedOut(): boolean {
  return getStorage<boolean>('justLoggedOut') === true;
}

/** True when the URL contains an OAuth authorization-code callback from Keycloak. */
export function hasOAuthCallbackInUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
}

